export interface NotificationGroupKeyInput {
	id: string
	threadId?: string | null
	entityType: string
	entityId: string
}

/** One inbox entry per thread (or other entity). Workspace events (billing, limits, imports) are unrelated, so each stands alone. */
export function notificationGroupKey(row: NotificationGroupKeyInput): string {
	if (row.threadId) return row.threadId
	return row.entityType === "workspace"
		? row.id
		: `${row.entityType}:${row.entityId}`
}

/** "Delete all completed": a thread in Done or Close-loop. */
export const COMPLETED_THREAD_STATES = ["PROCESSED", "COMPLETED"] as const

/** "Delete all completed": Linear issue status and project state types nobody works on any more. */
export const COMPLETED_ENTITY_STATUSES = [
	"completed",
	"canceled",
	"cancelled",
	"duplicate",
	"closed",
]

export interface NotificationRowGroup<T> {
	key: string
	/** Newest first; rows[0] is what the group shows. */
	rows: T[]
	unreadIds: string[]
}

/** Linear-style: one entry per thread (or other entity), in order of its newest event. */
export function groupNotificationRows<
	T extends NotificationGroupKeyInput & {
		createdAt: number
		readAt?: number | null
	},
>(rows: readonly T[]): NotificationRowGroup<T>[] {
	const groups = new Map<string, NotificationRowGroup<T>>()
	// Ties break on id like Zero's ordering, so a row kept after it left the query keeps its place.
	const sorted = [...rows].sort(
		(a, b) =>
			b.createdAt - a.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
	)
	for (const row of sorted) {
		const key = notificationGroupKey(row)
		let group = groups.get(key)
		if (!group) {
			group = { key, rows: [], unreadIds: [] }
			groups.set(key, group)
		}
		group.rows.push(row)
		if (row.readAt == null) group.unreadIds.push(row.id)
	}
	return [...groups.values()]
}

export function formatUnreadCount(count: number): string {
	return count > 99 ? "99+" : String(count)
}
